import { FormInput, TokenList } from '@/components';
import { Tray } from '@/components/atoms';
import {
  TrayNavigationProvider,
  useTrayNavigation,
} from '@/components/providers/common/TrayNavigationProvider';
import { useMarketData } from '@/components/providers/postauth/MarketDataProvider';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const { MAIN_GAP, MAIN_PADDING, MAIN_ROUNDEDNESS } = APP_CONFIG.STYLES;
interface BrowseAndSelectTokenTrayProps {
  isOpen: boolean;
  onClose: (selectedToken?: TokenInterface) => void;
  showNetwork?: boolean;
  showNetworkPrefix?: boolean;
}
const colorConfig = {
  backgroundColor: 'bg-primary/5',
  borderColor: 'border border-primary/5',
  headerTextColor: 'text-primary',
  subHeaderTextColor: 'text-secondary',
  bodyTextColor: 'text-secondary',
};
const roundednessConfig = {
  roundedness: `rounded${MAIN_ROUNDEDNESS}`,
};
const BrowseAndSelectTokenTrayContent: FC<BrowseAndSelectTokenTrayProps> = ({
  isOpen,
  onClose,
  showNetwork = true,
  showNetworkPrefix = true,
}) => {
  const { navigateBack } = useTrayNavigation();
  const { getTokens } = useTradeableTokens();
  const { isTopErc20Loading: marketDataLoading } = useMarketData();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);
  const filteredTokens = useMemo(() => {
    if (marketDataLoading) return [];
    const allTokens = getTokens('');
    const searchLower = debouncedSearchTerm.toLowerCase().trim();
    if (!searchLower) {
      return allTokens
        .slice()
        .sort((a, b) => (b.marketCapInUsd || 0) - (a.marketCapInUsd || 0));
    }
    const getScore = (token: TokenInterface): number => {
      const symbolLower = token.symbol.toLowerCase();
      const nameLower = token.name.toLowerCase();
      let score = 0;
      if (symbolLower === searchLower) score = Math.max(score, 100);
      if (symbolLower.startsWith(searchLower)) score = Math.max(score, 90);
      if (symbolLower.includes(searchLower)) score = Math.max(score, 80);
      if (nameLower === searchLower) score = Math.max(score, 50);
      if (nameLower.startsWith(searchLower)) score = Math.max(score, 40);
      if (new RegExp(`\\b${searchLower}\\b`).test(nameLower))
        score = Math.max(score, 30);
      if (nameLower.includes(searchLower)) score = Math.max(score, 20);
      return score;
    };
    return allTokens
      .map((token) => ({
        token,
        score: getScore(token),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => {
        if (a.score !== b.score) {
          return b.score - a.score;
        }
        const symbolA = a.token.symbol.toLowerCase();
        const symbolB = b.token.symbol.toLowerCase();
        if (symbolA !== symbolB) {
          return symbolA.localeCompare(symbolB);
        }
        return (b.token.marketCapInUsd || 0) - (a.token.marketCapInUsd || 0);
      })
      .map(({ token }) => token);
  }, [getTokens, debouncedSearchTerm, marketDataLoading]);
  const handleTokenSelect = useCallback(
    (token: TokenInterface) => {
      onClose(token);
    },
    [onClose],
  );
  const closeTray = () => {
    onClose();
  };
  const handleNavigateBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);
  const screens = useMemo(
    () => [
      {
        content: (
          <div
            className={`flex flex-col h-full gap${MAIN_GAP} ${borderClassToVisualizeElementsAndSpacing}`}
          >
            <div className="sticky top-0">
              <FormInput
                name="search"
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div
              className={`flex-1 overflow-y-auto p${MAIN_PADDING} ${colorConfig.backgroundColor} ${colorConfig.borderColor} ${roundednessConfig.roundedness}`}
            >
              <TokenList
                tokens={filteredTokens}
                applyRoundedMask
                showTicker
                showNetwork={showNetwork}
                showNetworkPrefix={showNetworkPrefix}
                onItemClick={handleTokenSelect}
                itemHeight={showNetwork ? 95 : 75}
              />
            </div>
          </div>
        ),
        onNavigateBack: handleNavigateBack,
        title: 'Select an asset',
        includeFooterCloseButton: true,
      },
    ],
    [
      MAIN_GAP,
      MAIN_PADDING,
      searchTerm,
      filteredTokens,
      colorConfig,
      roundednessConfig,
      handleTokenSelect,
      handleNavigateBack,
    ],
  );
  return (
    <Tray
      isOpen={isOpen}
      onClose={closeTray}
      screens={screens}
      className={`${borderClassToVisualizeElementsAndSpacing}`}
    />
  );
};
const BrowseAndSelectTokenTray: FC<BrowseAndSelectTokenTrayProps> = (props) => (
  <TrayNavigationProvider>
    <BrowseAndSelectTokenTrayContent {...props} />
  </TrayNavigationProvider>
);
export default BrowseAndSelectTokenTray;
